import { X } from "@phosphor-icons/react";
import { forwardRef, type MouseEvent } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { twRing } from "@/lib/emotionTw";
import { warningSurfaceTextStyle } from "@/components/ui/feedback/warningVisual";
import type { Tone } from "@/tokens";
import type { ChipProps, ChipSize, ChipVariant } from "./Chip.types";

interface ChipOwnerState {
  variant: ChipVariant;
  tone: Tone;
  size: ChipSize;
  selected: boolean;
  clickable: boolean;
}

/* Exact compiled output of the pre-migration Tailwind utilities, in
 * cva/tailwind-merge emission order (base → size → variant → selected →
 * clickable → variant×tone compounds). Verified against the frozen baseline.
 * The warning tone keeps its inline-style treatment (warningVisual). */

const chipBase: CSSObject = {
  display: "inline-flex",
  minWidth: "0px",
  alignItems: "center",
  borderRadius: "var(--bfrs-chip-radius)",
  fontWeight: 500,
  outline: "2px solid transparent",
  outlineOffset: "2px",
  transitionProperty:
    "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
  transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
  transitionDuration: ".15s"
};

const chipSizeStyles: Record<ChipSize, CSSObject> = {
  xs: {
    gap: "5px",
    borderRadius: "100px",
    paddingLeft: "6px",
    paddingRight: "6px",
    paddingTop: "2px",
    paddingBottom: "2px",
    fontSize: "10px",
    fontWeight: 400
  },
  sm: {
    minHeight: "1.5rem",
    gap: ".25rem",
    paddingLeft: ".5rem",
    paddingRight: ".5rem",
    fontSize: ".75rem",
    lineHeight: "1rem"
  },
  md: {
    minHeight: "1.75rem",
    gap: ".375rem",
    paddingLeft: ".625rem",
    paddingRight: ".625rem",
    fontSize: "12.5px"
  },
  lg: {
    minHeight: "2rem",
    gap: ".5rem",
    paddingLeft: ".75rem",
    paddingRight: ".75rem",
    fontSize: ".875rem",
    lineHeight: "1.25rem"
  }
};

const chipVariantStyles: Record<ChipVariant, CSSObject> = {
  default: {},
  soft: {},
  outline: {
    borderWidth: "1px",
    backgroundColor: "transparent"
  },
  filled: {}
};

const chipSelectedStyle: CSSObject = {
  "--tw-ring-offset-width": "0px",
  "--tw-ring-offset-color": "#fff",
  "--tw-ring-color": "hsl(var(--bfrs-color-primary) / .3)",
  "--tw-ring-offset-shadow": "0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color)",
  "--tw-ring-shadow": "0 0 0 calc(1px + var(--tw-ring-offset-width)) var(--tw-ring-color)",
  boxShadow: "var(--tw-ring-offset-shadow),var(--tw-ring-shadow),var(--tw-shadow, 0 0 #0000)"
};

const chipClickableStyle: CSSObject = {
  cursor: "pointer",
  "&:focus-visible": {
    ...twRing("2px", "hsl(var(--bfrs-color-border-focus) / var(--tw-ring-opacity, 1))"),
    "--tw-ring-opacity": "1"
  }
};

const border = (token: string): CSSObject => ({
  "--tw-border-opacity": "1",
  borderColor: `hsl(var(--bfrs-color-${token}) / var(--tw-border-opacity, 1))`
});
const bg = (token: string): CSSObject => ({
  "--tw-bg-opacity": "1",
  backgroundColor: `hsl(var(--bfrs-color-${token}) / var(--tw-bg-opacity, 1))`
});
const text = (token: string): CSSObject => ({
  "--tw-text-opacity": "1",
  color: `hsl(var(--bfrs-color-${token}) / var(--tw-text-opacity, 1))`
});

const softLikeTones: Record<Tone, CSSObject> = {
  neutral: { ...border("border"), ...bg("surface-app"), ...text("text-secondary") },
  primary: { ...border("primary-border"), ...bg("primary-soft"), ...text("primary") },
  brand: { ...border("primary-border"), ...bg("primary-soft"), ...text("primary") },
  success: { ...border("success-soft"), ...bg("success-soft"), ...text("success-contrast") },
  warning: {},
  danger: { ...border("danger-soft"), ...bg("danger-soft"), ...text("danger-contrast") },
  info: { ...border("info-soft"), ...bg("info-soft"), ...text("info-contrast") }
};

const chipCompoundStyles: Record<ChipVariant, Record<Tone, CSSObject>> = {
  default: softLikeTones,
  soft: softLikeTones,
  outline: {
    neutral: { ...border("border-strong"), ...text("text-secondary") },
    primary: { ...border("primary"), ...text("primary") },
    brand: { ...border("primary"), ...text("primary") },
    success: { ...border("success"), ...text("success") },
    warning: {},
    danger: { ...border("danger"), ...text("danger") },
    info: { ...border("info"), ...text("info") }
  },
  filled: {
    neutral: { ...border("text-secondary"), ...bg("text-secondary"), ...text("text-inverse") },
    primary: { ...border("primary"), ...bg("primary"), ...text("primary-contrast") },
    brand: { ...border("primary"), ...bg("primary"), ...text("primary-contrast") },
    success: { ...border("success"), ...bg("success"), ...text("primary-contrast") },
    warning: {},
    danger: { ...border("danger"), ...bg("danger"), ...text("danger-contrast") },
    info: { ...border("info"), ...bg("info"), ...text("primary-contrast") }
  }
};

const ChipRoot = styled("span", {
  label: "AgenticChip-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: ChipOwnerState }>(({ ownerState }) => [
  chipBase,
  chipSizeStyles[ownerState.size],
  chipVariantStyles[ownerState.variant],
  ownerState.selected && chipSelectedStyle,
  ownerState.clickable && chipClickableStyle,
  chipCompoundStyles[ownerState.variant][ownerState.tone]
]);

const ChipLabel = styled("span", {
  label: "AgenticChip-label"
})({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
});

const ChipRemoveButton = styled("button", {
  label: "AgenticChip-removeButton"
})({
  marginLeft: ".125rem",
  borderRadius: "var(--bfrs-radius-sm)",
  padding: ".125rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))",
  outline: "2px solid transparent",
  outlineOffset: "2px",
  "&:hover": {
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / .7)",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  },
  "&:focus-visible": {
    ...twRing("2px", "hsl(var(--bfrs-color-border-focus) / var(--tw-ring-opacity, 1))"),
    "--tw-ring-opacity": "1"
  },
  "&:disabled": {
    cursor: "not-allowed",
    opacity: ".5"
  }
});

export const Chip = forwardRef<HTMLSpanElement, ChipProps>(
  ({ className, variant = "default", tone = "neutral", size = "sm", leftIcon, rightIcon, selected, removable, onRemove, removeLabel = "Remove", children, onClick, style, ...props }, ref) => {
    const showRemove = Boolean(removable || onRemove);
    const ownerState: ChipOwnerState = {
      variant,
      tone,
      size,
      selected: Boolean(selected),
      clickable: Boolean(onClick)
    };

    return (
      <ChipRoot
        ref={ref}
        role={onClick ? "button" : props.role}
        tabIndex={onClick ? 0 : props.tabIndex}
        ownerState={ownerState}
        className={className}
        style={{ ...(tone === "warning" ? warningSurfaceTextStyle : undefined), ...style }}
        onClick={onClick}
        onKeyDown={(event) => {
          props.onKeyDown?.(event);
          if (onClick && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            onClick(event as unknown as MouseEvent<HTMLSpanElement>);
          }
        }}
        {...props}
      >
        {leftIcon}
        <ChipLabel>{children}</ChipLabel>
        {rightIcon}
        {showRemove && (
          <ChipRemoveButton
            type="button"
            aria-label={removeLabel}
            disabled={!onRemove}
            onClick={(event) => {
              event.stopPropagation();
              onRemove?.(event);
            }}
          >
            <X size={12} weight="bold" />
          </ChipRemoveButton>
        )}
      </ChipRoot>
    );
  }
);

Chip.displayName = "Chip";
